import React, { type ComponentPropsWithoutRef, useEffect, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useAssociateField } from '@atlassian/jira-business-inline-field-config-sidebar/src/controllers/associate-field/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { FlagRenderer, useFlagsService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag/src/index.tsx';
import {
	fireUIAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

type FixItErrorFlagProps = ComponentPropsWithoutRef<typeof ErrorFlag> & {
	fieldId: string;
	fieldName: string;
	issueType: string;
	issueTypeId: string;
};

interface FieldAssociationErrorFlagProps {
	issueType: string;
	issueTypeId: string;
	projectId?: number;
	fieldId: string;
	fieldName: string;
	isAdmin?: boolean;
}

export const FixItErrorFlag = ({
	error,
	title,
	description,
	issueType,
	issueTypeId,
	fieldId,
	fieldName,
}: FixItErrorFlagProps) => {
	const project = useProject();

	if (project.isSimplified) {
		const ASSOCIATION_OR_PERMISSION_ERROR = `Field '${fieldId}' cannot be set. It is not on the appropriate screen, or unknown.`;

		const errorMessage =
			error instanceof ValidationError
				? (error.errors && error.errors[0] && error.errors[0].error) || error.message
				: String(error);

		const isFixitError = errorMessage?.includes(ASSOCIATION_OR_PERMISSION_ERROR);

		const isEditPermissionError = isFixitError && !project.permissions.editIssues;
		if (isEditPermissionError) return <EditPermissionErrorFlag />;
		if (isFixitError)
			return (
				<FieldAssociationErrorFlag
					issueType={issueType}
					fieldName={fieldName}
					fieldId={fieldId}
					projectId={project.id}
					issueTypeId={issueTypeId}
					isAdmin={project.permissions.administerProject}
				/>
			);
	}

	return <ErrorFlag error={error} title={title} description={description} />;
};

const EditPermissionErrorFlag = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'flag shown', 'editPermissionErrorFlag');
	}, [createAnalyticsEvent]);

	return (
		<FlagRenderer
			flag={{
				type: 'warning',
				title: formatMessage(messages.editPermissionErrorTitle),
				actions: [
					{
						content: formatMessage(messages.editPermissionErrorAction),
						href: '/secure/ContactAdministrators!default.jspa',
						target: '_blank',
						onClick: () => {
							fireUIAnalytics(
								createAnalyticsEvent({}),
								'link clicked',
								'contactAdministratorFromEditPermissionFlag',
							);
						},
					},
				],
			}}
		/>
	);
};

const FieldAssociationErrorFlag = ({
	fieldId,
	fieldName,
	issueType,
	projectId,
	issueTypeId,
	isAdmin,
}: FieldAssociationErrorFlagProps) => {
	const { formatMessage } = useIntl();
	const { associate } = useAssociateField();
	const { dismissFlag } = useFlagsService();
	const [shouldAssociate, setShouldAssociate] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const dismissPreviousFlags = useRef(true);

	const FIXIT_ERROR_FLAG_ID = `FIXIT_ERROR_FLAG_FOR_${issueTypeId}_${fieldId}`;

	useEffect(() => {
		if (shouldAssociate) {
			setShouldAssociate(false);
			associate({
				input: {
					projectId: String(projectId),
					fieldId,
					issueTypeIds: [Number(issueTypeId)],
				},
				fieldName,
				issueType,
				showSuccess: true,
				analyticsAttributes: {
					source: 'business-fields.common.ui.fixit-error-flag',
				},
			}) // Replace with lodash/noop
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				.catch(() => {});
		}
	}, [associate, fieldId, fieldName, issueType, issueTypeId, projectId, shouldAssociate]);

	useEffect(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'flag shown', 'fixItErrorFlag');
	}, [createAnalyticsEvent]);

	const actions = isAdmin
		? [
				{
					content: formatMessage(messages.updateFieldAssociationAction),
					onClick: async () => {
						if (projectId) {
							dismissFlag(FIXIT_ERROR_FLAG_ID);
							// When associate service is called from the flag, it triggers refetch available fields internally, but
							// `refetch` function is `undefined`. This could be because the flag is mounted outside of the view component
							// where the available fields service hasn't been called yet (we can only refetch a service that has been called at least once).
							// Hence we need to make sure we call associate() inside of the cell.
							setShouldAssociate(true);
						}

						fireUIAnalytics(
							createAnalyticsEvent({}),
							'button clicked',
							'associateFieldFromFixItFlag',
						);
					},
				},
			]
		: [
				{
					content: formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.editIssueTypesPermissionErrorActionIssueTermRefresh
							: messages.editIssueTypesPermissionErrorAction,
					),
					href: '/secure/ContactAdministrators!default.jspa',
					target: '_blank',
					onClick: () => {
						fireUIAnalytics(
							createAnalyticsEvent({}),
							'link clicked',
							'contactAdministratorFromFixitFlag',
						);
					},
				},
			];

	// dismiss any existing flag with the same id
	if (dismissPreviousFlags.current) {
		dismissFlag(FIXIT_ERROR_FLAG_ID);
		dismissPreviousFlags.current = false;
	}

	return (
		<FlagRenderer
			flag={{
				id: FIXIT_ERROR_FLAG_ID,
				type: 'warning',
				title: [
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.editAssociationErrorTitleIssueTermRefresh
						: messages.editAssociationErrorTitle,
					{
						fieldName,
						issueType,
						normal: (chunks: string) => <FlagMessageWrapper>{chunks}</FlagMessageWrapper>,
						strong: (chunks: string) => <Text as="strong">{chunks}</Text>,
					},
				],
				actions,
			}}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlagMessageWrapper = styled.span({
	fontWeight: token('font.weight.regular'),
});
